// react
import React, { useState, useEffect } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// context
import { UserAuth } from "context/AuthContext";

// @mui
import { Box, IconButton, Dialog, Tooltip } from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// ui-components
import ImageTable from "./ImageTable";
import DataDialog from "ui-components/certification/DataDialog";
import DatapointIntegrityInspector from "ui-components/certification/DatapointIntegrityInspector";
import { LoadingDialog } from "ui-components/LoadingComponent";

// firebase
import { getFunctions, httpsCallable } from "firebase/functions";

// storage
import downloadFile from "storage/downloadFile";

// verification
import { verifySignature } from "verification";

// A ---------------------------------------------------------------------- M

const ImageViewer = ({ tag, isTagGroupMember }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const functions = getFunctions();

  const [record, setRecord] = useState();
  const [verification, setVerification] = useState();
  const [openVerify, setOpenVerify] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      if (openVerify) setOpenVerify(false);
      else if (openView) setOpenView(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [openVerify, openView]);

  const handleOpenView = () => {
    setOpenView(true);
    window.history.pushState(null, "");
  };

  const handleOpenVerify = () => {
    setOpenVerify(true);
    window.history.pushState(null, "");
  };

  const checkVerification = async (record) => {
    setIsLoading(true);
    try {
      const verification = user ? await verifySignature(user.uid, record, record.type) : await verifySignature(false, record, record.type);
      setVerification(verification);
      handleOpenVerify();
    } catch (err) {
      console.error("Error in checkVerification:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (record) => {
    setRecord(record);
    handleOpenView();
  };

  const handleCloseDialog = () => setSelectedImage(null);

  const handlePreview = async (storagePath) => {
    setIsLoading(true);
    try {
      const getUrl = httpsCallable(functions, "getSignedDownloadUrl");
      const { data } = await getUrl({ storagePath, tagId: tag.id });
      const signedUrl = data.url;

      const resp = await fetch(signedUrl);
      const blob = await resp.blob();
      const mime = blob.type.toLowerCase();

      if (mime === "image/png" || mime === "image/jpg" || mime === "image/jpeg") {
        setSelectedImage(signedUrl);
      } else {
        console.warn("Il file non è un’immagine png/jpg/jpeg valida:", mime);
      }
    } catch (err) {
      console.error("Error in handlePreview:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileClick = async (fileUrl) => {
    setIsLoading(true);
    try {
      const resp = await fetch(fileUrl);
      const blob = await resp.blob();
      const mime = blob.type.toLowerCase();

      if (mime === "image/png" || mime === "image/jpg" || mime === "image/jpeg") {
        setSelectedImage(fileUrl);
      } else {
        console.warn("Il file non è un’immagine png/jpg/jpeg valida:", mime);
      }
    } catch (err) {
      console.error("Error fetching and parsing file:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenFile = async (record) => {
    const data = record.data || {};
    if (data.storagePath) {
      await handlePreview(data.storagePath);
    } else if (data.attachment) {
      await handleFileClick(data.attachment);
    } else {
      console.warn("Nessun campo storagePath o attachment trovato nel record", record);
    }
  };

  const handleDownload = async (record) => {
    setIsLoading(true);
    try {
      const { storagePath, attachment, fileName } = record.data;

      if (storagePath) {
        const getUrl = httpsCallable(functions, "getSignedDownloadUrl");
        const { data } = await getUrl({ storagePath, tagId: tag.id });
        await downloadFile(data.url, fileName || "downloaded_file");
      } else if (attachment) {
        await downloadFile(attachment, fileName || "downloaded_file");
      } else {
        console.warn("Nessun campo storagePath o attachment trovato per il download", record);
      }
    } catch (err) {
      console.error("Error in handleDownload:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <LoadingDialog open={isLoading} />}

      {tag && <ImageTable tag={tag} isTagGroupMember={isTagGroupMember} checkVerification={checkVerification} handleView={handleView} />}

      {record && <DataDialog data={{ ...record, databoxName: tag.name || "N/A" }} open={openView} onClose={() => setOpenView(false)} handleOpenFile={handleOpenFile} handleDownload={handleDownload} />}

      {verification && <DatapointIntegrityInspector verification={verification} open={openVerify} setOpen={setOpenVerify} />}

      {selectedImage && (
        <Dialog open={Boolean(selectedImage)} onClose={handleCloseDialog} fullWidth maxWidth="md">
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip placement="top" title={t("close")}>
              <IconButton
                color="error"
                sx={{
                  position: "absolute",
                  top: "0%",
                  right: "0%",
                  zIndex: 1,
                }}
                onClick={handleCloseDialog}
              >
                <CloseOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <img src={selectedImage} alt="img" />
        </Dialog>
      )}
    </>
  );
};

export default ImageViewer;
